// v1
// Path: frontend/src/components/SyncHub/shared/StandardSetModals.jsx
// Inputs: Modal open states, entity catalogs, table lists, and submit callbacks
// Outputs: Accessible modal dialogs for Set creation, Set rename, Pipeline creation, and Pipeline rename.
// Summary: Modal dialog components for managing mapping sets and pipeline presets with light dismiss and validation.

import React, { useState, useEffect } from 'react';
import SearchableSelect from '../SearchableSelect';
import { getQbEntityMeta, normalizeEntityName } from '../entityCatalogs';

// ── 1. Create New Standard Set Modal ──────────────────────────────────────────
export function NewSetModal({
    isOpen,
    onClose,
    availableSets = [],
    onSubmit
}) {
    const [setName, setSetName] = useState('');
    const [cloneFrom, setCloneFrom] = useState('__NONE__');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setSetName('');
            setCloneFrom('__NONE__');
            setError(null);
            setSaving(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = setName.trim();
        if (!trimmed) return;
        setSaving(true);
        setError(null);
        try {
            await onSubmit({ newSetName: trimmed, cloneFromSetName: cloneFrom === '__NONE__' ? null : cloneFrom });
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to create set');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            className="fixed inset-0 z-60 flex items-center justify-center bg-black/80 p-4"
        >
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md shadow-2xl space-y-4">
                <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-amber-400">add_circle</span>
                        Create Standard Mapping Set
                    </h3>
                    <button type="button" onClick={onClose} className="text-slate-400 hover:text-white text-lg">&times;</button>
                </div>

                {error && (
                    <div className="p-2.5 bg-rose-950/80 border border-rose-800 text-rose-300 rounded text-xs">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-slate-300 mb-1">Set Name *</label>
                        <input
                            type="text"
                            value={setName}
                            onChange={e => setSetName(e.target.value)}
                            placeholder="e.g. Retail Standard, Wholesale Profile, Xero Standard"
                            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
                            required
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-slate-300 mb-1">Clone from Existing Set</label>
                        <select
                            value={cloneFrom}
                            onChange={e => setCloneFrom(e.target.value)}
                            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500 cursor-pointer"
                        >
                            <option value="__NONE__">-- Start Blank (No Clone) --</option>
                            {availableSets.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                        <p className="text-[11px] text-slate-500 mt-1">
                            Optionally copies all entity field mappings from an existing standard set into the new set.
                        </p>
                    </div>

                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving || !setName.trim()}
                            className="px-4 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition-colors"
                        >
                            {saving ? 'Creating...' : 'Create Set'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ── 2. Rename Standard Set Modal ──────────────────────────────────────────────
export function RenameSetModal({
    isOpen,
    onClose,
    currentSetName,
    onSubmit
}) {
    const [newName, setNewName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setNewName(currentSetName || '');
            setError(null);
            setSaving(false);
        }
    }, [isOpen, currentSetName]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = newName.trim();
        if (!trimmed || trimmed === currentSetName) return;
        setSaving(true);
        setError(null);
        try {
            await onSubmit(trimmed);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to rename set');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            className="fixed inset-0 z-60 flex items-center justify-center bg-black/80 p-4"
        >
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md shadow-2xl space-y-4">
                <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-amber-400">edit</span>
                        Rename Standard Set
                    </h3>
                    <button type="button" onClick={onClose} className="text-slate-400 hover:text-white text-lg">&times;</button>
                </div>

                {error && (
                    <div className="p-2.5 bg-rose-950/80 border border-rose-800 text-rose-300 rounded text-xs">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-slate-300 mb-1">Current Name</label>
                        <div className="text-xs text-amber-300 font-bold px-3 py-2 bg-slate-800/60 rounded-lg border border-slate-700/60">
                            {currentSetName}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-slate-300 mb-1">New Set Name *</label>
                        <input
                            type="text"
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            placeholder="Enter new set name..."
                            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
                            required
                            autoFocus
                        />
                    </div>

                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving || !newName.trim() || newName.trim() === currentSetName}
                            className="px-4 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition-colors"
                        >
                            {saving ? 'Renaming...' : 'Rename Set'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ── 3. Add New Pipeline / Preset Modal ─────────────────────────────────────────
export function NewPipelineModal({
    isOpen,
    onClose,
    setName,
    sourceSystem = 'Passport',
    targetSystem = 'QuickBooks',
    targetEntities = [],
    availableTables = [],
    onSubmit
}) {
    const [selectedEntity, setSelectedEntity] = useState('Customer');
    const [selectedTable, setSelectedTable] = useState('');
    const [pipelineName, setPipelineName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            const defaultEnt = targetEntities[0]?.entity || 'Customer';
            const meta = getQbEntityMeta(defaultEnt);
            setSelectedEntity(defaultEnt);
            setSelectedTable(meta?.defaultTable || '');
            setPipelineName(`${setName} - ${sourceSystem} ${defaultEnt} -> ${targetSystem}`);
            setError(null);
            setSaving(false);
        }
    }, [isOpen, setName, sourceSystem, targetSystem, targetEntities]);

    if (!isOpen) return null;

    const handleEntityChange = (entityVal) => {
        const canonical = normalizeEntityName(entityVal);
        setSelectedEntity(canonical);
        const meta = getQbEntityMeta(canonical);
        if (meta?.defaultTable) {
            setSelectedTable(meta.defaultTable);
        }
        setPipelineName(`${setName} - ${sourceSystem} ${canonical} -> ${targetSystem}`);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedEntity || !pipelineName.trim()) return;
        setSaving(true);
        setError(null);
        try {
            await onSubmit({
                targetEntity: selectedEntity,
                sourceTable: selectedTable.trim().toUpperCase(),
                pipelineName: pipelineName.trim()
            });
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to create pipeline');
        } finally {
            setSaving(false);
        }
    };

    const tableOptions = (availableTables || []).map(t => ({
        value: typeof t === 'string' ? t : t.name,
        label: typeof t === 'string' ? t : (t.fieldCount ? `${t.name} (${t.fieldCount} cols)` : t.name),
        description: typeof t === 'object' ? t.description : undefined
    }));

    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            className="fixed inset-0 z-60 flex items-center justify-center bg-black/80 p-4"
        >
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-lg shadow-2xl space-y-4">
                <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-indigo-400">add_chart</span>
                        Add Pipeline to {setName}
                    </h3>
                    <button type="button" onClick={onClose} className="text-slate-400 hover:text-white text-lg">&times;</button>
                </div>

                {error && (
                    <div className="p-2.5 bg-rose-950/80 border border-rose-800 text-rose-300 rounded text-xs">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Target Entity */}
                    <div>
                        <label className="block text-xs font-medium text-slate-300 mb-1">Target Entity *</label>
                        <select
                            value={selectedEntity}
                            onChange={e => handleEntityChange(e.target.value)}
                            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-indigo-500 cursor-pointer"
                        >
                            {targetEntities.map(ent => {
                                const val = typeof ent === 'string' ? ent : (ent.entity || ent.name);
                                const label = typeof ent === 'string' ? ent : (ent.label || ent.name || ent.entity);
                                return (
                                    <option key={val} value={val}>{label}</option>
                                );
                            })}
                        </select>
                    </div>

                    {/* Source Table */}
                    <div>
                        <label className="block text-xs font-medium text-slate-300 mb-1">ERP Source Table</label>
                        <SearchableSelect
                            options={tableOptions}
                            value={selectedTable}
                            onChange={val => setSelectedTable(val || '')}
                            placeholder="Select or enter source table..."
                            allowCustom={true}
                            className="w-full"
                        />
                    </div>

                    {/* Pipeline / Preset Name */}
                    <div>
                        <label className="block text-xs font-medium text-slate-300 mb-1">Pipeline Name *</label>
                        <input
                            type="text"
                            value={pipelineName}
                            onChange={e => setPipelineName(e.target.value)}
                            placeholder="Pipeline preset name..."
                            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-indigo-500"
                            required
                        />
                    </div>

                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving || !selectedEntity || !pipelineName.trim()}
                            className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition-colors"
                        >
                            {saving ? 'Creating...' : 'Create Pipeline'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ── 4. Rename Pipeline / Preset Modal ─────────────────────────────────────────
export function RenamePipelineModal({
    isOpen,
    onClose,
    currentName,
    onSubmit
}) {
    const [newName, setNewName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setNewName(currentName || '');
            setError(null);
            setSaving(false);
        }
    }, [isOpen, currentName]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = newName.trim();
        if (!trimmed || trimmed === currentName) return;
        setSaving(true);
        setError(null);
        try {
            await onSubmit(trimmed);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to rename pipeline');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            className="fixed inset-0 z-60 flex items-center justify-center bg-black/80 p-4"
        >
            <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md shadow-2xl space-y-4">
                <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-indigo-400">edit</span>
                        Rename Pipeline Preset
                    </h3>
                    <button type="button" onClick={onClose} className="text-slate-400 hover:text-white text-lg">&times;</button>
                </div>

                {error && (
                    <div className="p-2.5 bg-rose-950/80 border border-rose-800 text-rose-300 rounded text-xs">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-slate-300 mb-1">Current Name</label>
                        <div className="text-xs text-indigo-300 font-bold px-3 py-2 bg-slate-800/60 rounded-lg border border-slate-700/60">
                            {currentName}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-slate-300 mb-1">New Pipeline Name *</label>
                        <input
                            type="text"
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            placeholder="Enter new pipeline name..."
                            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-indigo-500"
                            required
                            autoFocus
                        />
                    </div>

                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving || !newName.trim() || newName.trim() === currentName}
                            className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition-colors"
                        >
                            {saving ? 'Renaming...' : 'Rename Pipeline'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
